<template>
  <input
    :id="props.id"
    ref="inputRef"
    :class="DS_FILE_UPLOAD_CLASSES.Input"
    data-cy="ds-file-upload__input"
    type="file"
    :accept="props.acceptAttribute"
    :multiple="props.multiple"
    :disabled="props.disabled"
    :required="props.required"
    tabindex="-1"
    @change="onFileInputChange"
  />

  <div
    ref="dropzoneElementRef"
    :class="DS_FILE_UPLOAD_CLASSES.Dropzone"
    data-cy="ds-file-upload__dropzone"
    role="button"
    :tabindex="props.disabled ? undefined : 0"
    @click="openFileDialog"
    @keydown.enter.prevent="openFileDialog"
    @keydown.space.prevent="openFileDialog"
    @focus="onFocus"
    @blur="onBlur"
  >
    <DSIcon
      :class="DS_FILE_UPLOAD_CLASSES.Icon"
      icon="upload"
      size="xl"
    />
    <p
      :class="DS_FILE_UPLOAD_CLASSES.Title"
      data-cy="ds-file-upload__title"
    >
      {{ fileUploadTitle }}
    </p>
    <p
      :class="DS_FILE_UPLOAD_CLASSES.Description"
      data-cy="ds-file-upload__description"
    >
      {{ fileUploadDescription }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { DSFileUploadDropzoneEmits } from "../types/emits";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import { DS_FILE_UPLOAD_CLASSES } from "../constants/classes";
import { buildFileUploadDescription } from "../utils/fileUploadDescription";
import { resetNativeFileInput, setNativeFileInputFiles } from "../utils/nativeFileInput";

interface DSFileUploadDropzoneProps {
  id: string;
  acceptAttribute: string;
  multiple: boolean;
  disabled: boolean;
  required: boolean;
  maxFileSize?: number;
  description?: string;
}

const props = defineProps<DSFileUploadDropzoneProps>();
const emit = defineEmits<DSFileUploadDropzoneEmits>();
const inputRef = ref<HTMLInputElement>();
const dropzoneElementRef = ref<HTMLElement>();
const fileUploadTitle = computed(() => window.I18n.designSystem.fileUpload.title);
const fileUploadDescription = computed(() =>
  buildFileUploadDescription({
    acceptAttribute: props.acceptAttribute,
    maxFileSize: props.maxFileSize,
    description: props.description,
    translations: {
      supportedFormats: window.I18n.designSystem.fileUpload.descriptionSupportedFormats,
      maxFileSize: window.I18n.designSystem.fileUpload.descriptionMaxFileSize,
    },
  })
);

function onFileInputChange(event: Event): void {
  const target = event.target as HTMLInputElement;

  emit("files-selected", Array.from(target.files ?? []), event);
}

function onFocus(event: FocusEvent): void {
  emit("focus", event);
}

function onBlur(event: FocusEvent): void {
  emit("blur", event);
}

const { focus, blur } = useDSInput(computed(() => (props.disabled ? null : dropzoneElementRef.value)));

function openFileDialog(): void {
  if (props.disabled) return;

  inputRef.value?.click();
}

function resetInput(): void {
  if (inputRef.value) {
    resetNativeFileInput(inputRef.value);
  }
}

function setInputFiles(files: File[]): void {
  if (!inputRef.value) return;

  setNativeFileInputFiles(inputRef.value, files);
}

defineExpose({
  blur,
  focus,
  openFileDialog,
  resetInput,
  setInputFiles,
});
</script>
